import * as React from 'react';
import {
  quotesDownloadPdf,
  quotesPayInvoiceClick478,
  quotesPrintInvoice477,
  quotesViewInvoicePageView,
} from '@wix/bi-logger-dash/v2';
import { InvoiceStatus } from '@wix/ambassador-wix-quotes-web/types';
import { BaseLogger } from '@wix/fedops-logger';
import webBiLogger from '@wix/web-bi-logger';
import { InvoiceViewV2, isNewMobileView } from '@wix/price-quote-view';

import s from './client.scss';
import { INVOICE_VIEW } from './constants';
import { InvoiceActions } from './InvoiceActions/InvoiceActions';
import { historicInvoiceSettings } from '../../handlers/invoiceSettings';
import {
  extractBusinessCustomFields,
  extractDocumentCustomFields,
} from '../../../api/mapCustomFields';
import { PaymentType } from '../../../constants/bi';
import { Experiments } from '../../../constants/experiments';

const invoiceSettings = window.__INVOICE_SETTINGS__ || historicInvoiceSettings;
const logger = webBiLogger.factory().logger();

interface InvoiceViewClientProps {
  invoice: any;
  businessInfo: any;
  status: InvoiceStatus;
  isUoU: boolean;
  invoiceId: string;
  instanceId: string;
  invoiceVersion: number;
  token: string;
  hasPaymentMethods: boolean;
  recurringInvoiceViewEnabled: boolean;
  data: {
    msid: string;
    experiments: any;
  };
  footer: { notesLabel: string; legalTermsLabel: string };
  invoiceNumberLabel: string;
  fedopsLogger: BaseLogger<string>;
  isPremium: boolean;
}

export class InvoiceViewClient extends React.Component<InvoiceViewClientProps> {
  componentDidMount() {
    const {
      invoice: {
        customer: { contactId: uuid },
        metaData: { source },
      },
      status,
      invoiceId: invoice_id,
      hasPaymentMethods,
      recurringInvoiceViewEnabled,
      data: { msid },
    } = this.props;

    logger.report(
      quotesViewInvoicePageView({
        invoice_id,
        paid: status === 'Paid',
        origin: 'customer',
        is_visible: hasPaymentMethods,
        paymentType: recurringInvoiceViewEnabled
          ? PaymentType.RECURRING
          : PaymentType.ONE_TIME,
        invoiceSource: source,
        msid,
        // @ts-expect-error Uncatalogued BI field
        uuid,
      }),
    );
    this.props.fedopsLogger.interactionEnded(INVOICE_VIEW);
  }

  private reportPdfDownloadBI(paymentType: PaymentType) {
    const {
      invoice: {
        customer: { contactId: uuid },
      },
      invoiceId: invoice_id,
      data: { msid },
    } = this.props;

    logger.report(
      quotesDownloadPdf({
        invoice_id,
        origin: 'customer',
        paymentType,
        msid,
        // @ts-expect-error Uncatalogued BI field
        uuid,
      }),
    );
  }

  private reportPrintBI(paymentType: PaymentType) {
    const {
      invoiceId: invoice_id,
      data: { msid },
    } = this.props;

    logger.report(
      quotesPrintInvoice477({
        invoice_id,
        origin: 'customer',
        paymentType,
        msid,
      }),
    );
  }

  reportPayBI = async (paymentType: PaymentType) => {
    const {
      invoiceId: invoice_id,
      invoice,
      data: { msid },
    } = this.props;
    const printBiPayload = {
      invoice_id,
      invoiceSource: invoice.metaData.source,
      externalId: invoice.metaData.sourceRefId,
      origin: 'customer',
      paymentType,
      totalAmountLong:
        invoice.totals &&
        invoice.totals.total &&
        Math.round(invoice.totals.total.serializedValue * 100000),
      msid,
    };

    await logger.report(quotesPayInvoiceClick478(printBiPayload));
  };

  renderInvoiceActions = (setup?: {
    hidePdfDownloader?: boolean;
    mobileInvoiceViewV2Enabled?: boolean;
  }) => {
    const {
      invoice,
      status,
      instanceId,
      invoiceId,
      token,
      invoiceVersion,
      hasPaymentMethods,
      recurringInvoiceViewEnabled,
      isPremium,
    } = this.props;

    const urlPrefix = window.__URL_PREFIX__;
    const consentPolicyEnabled = window.__CONSENT_POLICY_ENABLED__;
    const hasBalance =
      invoice.dynamicTotals.balance &&
      !!invoice.dynamicTotals.balance.serializedValue;
    const pdfUrl = `${
      consentPolicyEnabled ? urlPrefix : ''
    }/invoice/${instanceId}:${invoiceId}/pdf/view`;
    const payUrl = `${
      consentPolicyEnabled ? urlPrefix : ''
    }/invoice/pay/${instanceId}:${invoiceId}`;
    const payRecurringUrl = `${
      consentPolicyEnabled ? urlPrefix : ''
    }/invoice/pay/recurring/${instanceId}:${invoiceId}`;
    const withPay =
      hasPaymentMethods &&
      !['Paid', 'Void', 'Processing'].includes(status) &&
      hasBalance;
    const paymentType = recurringInvoiceViewEnabled
      ? PaymentType.RECURRING
      : PaymentType.ONE_TIME;
    const newMobileViewEnabled = isNewMobileView();

    return (
      <InvoiceActions
        withPay={withPay}
        pdfUrl={pdfUrl}
        payUrl={recurringInvoiceViewEnabled ? payRecurringUrl : payUrl}
        token={token}
        print={() => {
          this.reportPrintBI(paymentType);
          window.print();
        }}
        setup={setup}
        version={invoiceVersion}
        onDownloadPDF={() => this.reportPdfDownloadBI(paymentType)}
        onPayForm={async () => {
          this.props.fedopsLogger.interactionStarted('pay-invoice-navigation');
          await this.reportPayBI(paymentType);
        }}
        newMobileViewEnabled={newMobileViewEnabled}
        isPremium={isPremium}
      />
    );
  };

  render() {
    const {
      invoice,
      businessInfo,
      status,
      recurringInvoiceViewEnabled,
      isUoU,
      footer,
      invoiceNumberLabel,
      data: { experiments },
    } = this.props;

    const newMobileViewEnabled = isNewMobileView();
    const taxAdjustmentsEnabled =
      experiments?.experiments?.[Experiments.TaxAdjustments] === 'true';
    const paymentsSectionEnabled =
      experiments?.experiments?.[Experiments.PaymentsSection] === 'true';

    const showInvoiceActions = !(isUoU && newMobileViewEnabled);

    return (
      <div
        data-hook="invoice-view-client"
        className={s.container}
        style={
          window.__INITIAL_I18N__.locale === 'ja'
            ? ({ '--wsr-font-family': 'Noto Sans CJK JP Regular' } as any)
            : {}
        }
      >
        {showInvoiceActions && (
          <div className={s.actionsHeader}>{this.renderInvoiceActions()}</div>
        )}
        <div className={showInvoiceActions ? s.content : ''}>
          <InvoiceViewV2
            recurringInvoiceViewEnabled={recurringInvoiceViewEnabled}
            recurringStandaloneViewEnabled={true}
            invoice={invoice}
            businessInfo={businessInfo}
            status={status}
            isUoU={isUoU}
            documentCustomFields={extractDocumentCustomFields(
              invoice.customFields || [],
            )}
            businessCustomFields={extractBusinessCustomFields(
              invoice.customFields || [],
            )}
            footer={footer}
            customInvoiceNumberLabel={invoiceNumberLabel}
            customFields={invoice.customFields || []}
            renderInvoiceActions={this.renderInvoiceActions}
            showTaxPerLineItem={invoiceSettings.showTaxPerLineItem}
            showTaxBreakdown={invoiceSettings.showTaxBreakdown}
            showItemsSubtotalPerTax={invoiceSettings.showItemsSubtotalPerTax}
            showTransactionDetails={invoiceSettings.showTransactionDetails}
            taxSettingsEnabled={invoiceSettings.enabled}
            taxAdjustmentsEnabled={taxAdjustmentsEnabled}
            paymentsSectionEnabled={paymentsSectionEnabled}
          />
        </div>
      </div>
    );
  }
}
